// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import mitt from 'mitt'

import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'


// import { LMap, LTileLayer, LMarker } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { Icon } from 'leaflet'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// Vue.component('l-map', LMap)
// Vue.component('l-tile-layer', LTileLayer)
// Vue.component('l-marker', LMarker)

import router from '@/router/index'
import axios from '@/lib/axiosClient'

import App from '@/App'

import {appNow} from '@/lib/appTime'
import VueCookies from 'vue-cookies'

import {COOKIE, IS_DEV, HTTP_PROTOCOL} from '@/components/Data'
import {API_BASE_URL, API_KEY} from '@/lib/env'

import AccessMixins from '@/mixins/AccessMixins'
import * as authLib from '@/lib/auth'
import * as sessionLib from '@/lib/session'
import * as appConfig from '@/lib/appConfig'
import { installSyncNotices } from '@/lib/syncNotices'
import { installEscCancel } from '@/lib/escCancel'
import { createLeaveGuard, installPageLeave } from '@/lib/leaveGuard'
import { createAuthGuard } from '@/lib/authGuard'
import { clearLeaveBypass } from '@/lib/requestRowSync'
import { brandingDiffers, brandingToBaseMap } from '@/lib/clientBranding'
import { stripSensitiveState } from '@/lib/persistState'
import safeHtml from '@/directives/safeHtml'

window.jQuery = window.$ = require('jquery')

Vue.use(Vuex)
Vue.use(VueCookies)
Vue.directive('safe-html', safeHtml)
Vue.prototype.$events = mitt()

Vue.config.productionTip = false

// Remove old config, exposes too much info.
if (localStorage.getItem(sessionLib.CONFIG_CACHE_KEY)) {
  localStorage.removeItem(sessionLib.CONFIG_CACHE_KEY)
}

// XOL-446: the config singleton is owned by lib/appConfig.js so it can be
// reset on logout and unit-tested (main.js is unimportable under Jest).
appConfig.hydrateConfigFromSession()

/**
 * The environment-derived slice of the store: everything computed from the
 * build and the current location rather than restored from storage.
 *
 * XOL-446: this is passed to buildBootState() at boot AND re-applied by the
 * session teardown's reset, so a reset can no longer silently drop
 * `endpoint` / `hostname` / `copyright` the way the old resetStore() did.
 */
function bootEnvironment () {
  var endpoint
  // KAN-39 / XOL-225: API_BASE_URL is the single source of truth for the local
  // API endpoint. Set it via `yarn set-api-port <port>` (writes a gitignored
  // root .env), which config/dev.env.js picks up through build/load-env.js.
  if (API_BASE_URL) {
    endpoint = API_BASE_URL.replace(/\/+$/, '') + '/'
  } else {
    endpoint = IS_DEV ? 'http://localhost:8000/' : HTTP_PROTOCOL + location.hostname + '/'
  }
  return {
    env: process.env.NODE_ENV,
    endpoint: endpoint,
    hostname: IS_DEV ? 'http://localhost:8080/' : HTTP_PROTOCOL + location.hostname + '/',
    copyright: 'xolas.io ' + appNow().format('YYYY'),
    windowWidth: window.width,
    windowHeight: window.height
  }
}

// XOL-446: buildBootState() deep-copies a factory-built default rather than
// aliasing the `defaultStore` module literal. The old `value = defaultStore`
// made store.state === defaultStore, so setTokens() wrote live JWTs into the
// module object and any clone-the-defaults reset resurrected them.
var value = sessionLib.buildBootState(JSON.parse(localStorage.getItem(COOKIE)), bootEnvironment())

const store = new Vuex.Store(
  {
    // KAN-39: do NOT persist the auth slice via vuex-persistedstate.
    // Tokens live in localStorage (shared across tabs since XOL-151) and are hydrated below.
    plugins: [createPersistedState({
      key: COOKIE,
      reducer: stripSensitiveState
    })],
    state: value
  }
)

// KAN-39: wire lib/auth.js to the Vuex store, then hydrate tokens
// from localStorage so reload (and new tabs) do not log the user out.
authLib.setStore(store)
// XOL-446: same handshake for the teardown, so endSession() can reset state
// it does not own without lib/session.js importing main.js (which would be a
// cycle — main.js imports axiosClient.js, which imports session.js).
sessionLib.setStore(store)
authLib.hydrateFromStorage()
if (authLib.isAuthenticated()) {
  store.state.isLoggedIn = true
}

// KAN-39: Authorization: Bearer is injected by the axios request interceptor
// in src/lib/axiosClient.js for authenticated endpoints. X-API-Key remains
// the bootstrap credential for pre-JWT endpoints (POST /api/login,
// /api/register) because the API's XolasAuth middleware still gates them on
// it. The middleware prefers Bearer when present, so sending both on
// authenticated requests is harmless.
axios.defaults.headers.common['X-API-Key'] = API_KEY
axios.defaults.headers.common['Accept'] = 'application/json'

if (IS_DEV) {
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('XDEBUG')) {
    axios.defaults.params = {
      'XDEBUG_TRIGGER': '1'
    }
  }
}

Vue.mixin(
  {
    computed: {
      screenWidth: function () {
        return this.getStore().windowWidth
      },

      isPhone: function () {
        return this.XSmall
      },

      isMobile: function () {
        return this.isSmall
      },

      isTablet: function () {
        return this.isMedium
      },

      isLaptop: function () {
        return this.isLarge
      },

      isDesktop: function () {
        return this.isHuge
      },

      isXSmall: function () {
        return this.$mq === 'xs'
      },

      isSmall: function () {
        return this.$mq === 'sm'
      },

      isSmallOrMore: function () {
        return !this.isXSmall
      },

      isMediumOrLess: function () {
        return (this.isMedium || this.isSmall || this.isXSmall)
      },

      isMedium: function () {
        return this.$mq === 'md'
      },

      isMediumOrMore: function () {
        return (this.isMedium || this.isLarge || this.isHuge)
      },

      isLarge: function () {
        return this.$mq === 'lg'
      },

      isLargeOrMore: function () {
        return (this.isLarge || this.isHuge)
      },

      isHuge: function () {
        return this.$mq === 'hg'
      },

      company: function () {
        if (this.org()) {
          return this.org().name
        } else {
          return 'No name'
        }
      }

    },

    methods: {

      /**
       Authenticated bootstrap: org config + the full client org.

       XOL-446: this used to run unconditionally from App.vue's created(),
       firing `GET system/changes` and `GET system/config` before login. Both
       sit inside the API's JWT-gated route group (routes/api.php), so a cold
       pre-login boot produced two guaranteed 401s — noise that also had to be
       specially tolerated by the forced-logout path. Now it early-returns
       when unauthenticated, and pre-auth branding is Login.vue's job (see
       refreshClientBranding below).

       The `system/changes` block that used to live here was deleted: it was
       already documented as dead code (axios.all() was passed URL strings
       rather than Promises, and its index was out of bounds after the loop),
       so it never populated meta.
       */
      init () {

        if (!authLib.isAuthenticated()) {
          return false
        }

        this.updateConfig()
        this.fetchFullClient()

        return false
      },


      /**
       The full, JWT-gated client org — includes contacts/email/phone, which
       the pre-auth branding whitelist deliberately omits. Always resolves so
       a client-fetch failure can never block login.
       */
      fetchFullClient () {

        return axios.get(this.getEndpoint('system/client')).then(response => {

          var data = response.data
          if (data) {
            store.state.xolasClient = data
            store.state.baseMap = brandingToBaseMap(data)
            this.saveStore()
          }
          return true

        }, e => {
          console.error('Error Loading Client Info: ', e)
          return false
        })
      },


      /**
       Pre-auth branding for the login screen (XOL-311's client.gate-only
       whitelist: name, shortName, logoPath, colourCode, latitude, longitude,
       contactsCount).

       XOL-446: called on every login-screen mount, so a client swap or a DB
       restore can never leave the previous deployment's logo on screen. The
       payload is compared field-wise first and only written when it actually
       differs — otherwise a repeat mount would churn Vuex and localStorage
       for nothing. See lib/clientBranding.js for why this is not a
       JSON.stringify compare.

       Always resolves, to a boolean, so callers can use it purely as a
       "settled" signal.
       */
      refreshClientBranding () {

        return axios.get(this.getEndpoint('client')).then(response => {

          var data = response.data

          if (brandingDiffers(this.getStore().xolasClient, data)) {
            this.getStore().xolasClient = data
            this.getStore().baseMap = brandingToBaseMap(data)
            this.saveStore()
          }
          return true

        }, e => {
          // Leave the cached branding in place — a stale logo beats none, and
          // login has no dependency on it.
          console.error('Error Loading Client Branding: ', e)
          return false
        })
      },


      refreshBrowser () {
        window.location.reload()
      },



      /**
       @param {String} path - reponse item from server.
       @param {Number} id = null

       @return {String} - valid path to server

       Wraps the path with the full host name, HTTP protocol
       */
      getEndpoint (path, id = null, attachOrg = true, checkSessionExp = true) {

        if (!path.startsWith('/')) {
          path = '/' + path
        }

        // KAN-39: removed legacy `api_token_expiry` soft check.
        // Session validity is enforced by the JWT `exp` claim and the
        // axios interceptor (silent refresh + reactive retry).

        if (attachOrg && this.org()) {

          if (id) {
            path = path + '/' + id
          }

          let orgId = null
          let orgCategory = null

          if (this.org().id) {
            orgId = this.org().id
          }

          if (this.org().category) {
            orgCategory = this.org().category.trim()
          }

          if (path.indexOf('orgCategory') === -1) {
             let pr = path.indexOf('?') > 0 ? '&' : '?'
            path = path + (orgId ? pr + 'orgId=' + orgId + (orgCategory ? '&orgCategory=' + orgCategory : '') : '')
          }

        }

        return store.state.endpoint + 'api' + path
      },



      /**
       @param {Object} response - reponse item from server.
       @param {Boolean} showMessage = true - the ID of the record to delete.
       @param {String} title = 'Operation Failed to Complete'
       @param {String} timeout = 6500 - in milisseconds

       Checks the reponse for 'error' or 'warning' props, and shows relevant
       message to the user.
       */
      checkWarning (response, showMsg = true, title = 'Operation Failed to Complete', timeout = 6500) {

        if (response && response.status !== 200 && response.data && (response.data.warning || response.data.error)) {

          if (showMsg) {

            this.$notify(
              {
                title: response.data.title ? response.data.title : title,
                message: response.data.message ? response.data.message : (response.data.error ? response.data.error : response.data.warning),
                type: response.data.error ? 'error' : 'warning',
                duration: timeout
              })
          }
          return true
        }

        return false
      },



      // -- Config stuff ------------------------------------------------

      // XOL-446: the config singleton lives in lib/appConfig.js so it can be
      // reset by the session teardown and covered by unit tests. These two
      // stay on the mixin because ~30 call sites across 27 components use
      // them; they are now thin delegations.
      getConfig () {
        return appConfig.getConfig()
      },



      updateConfig () {
        // Get config.
        var configUrl = this.getEndpoint('system/config')

        return axios.get(configUrl).then(response => {
          appConfig.setConfig(response.data)
        })
        .catch(e => {
          // KAN-39: 401 handled centrally in src/lib/axiosClient.js
          // XOL-357: this always resolves, so a failed config fetch still
          // lets login complete and navigate.
          console.error('Error Loading Config Info: ', e)
        })
      },


      // -- Store stuff ------------------------------------------------

      getStore () {
        return store.state
      },



      saveStore () {
        // XOL-446: saveStore() is the ONLY post-boot writer of
        // localStorage[COOKIE] — vuex-persistedstate persists via
        // store.subscribe, which fires on mutations, and this app commits
        // none. So this guard is what stops a late XHR callback from writing
        // the outgoing user's state back into the key the teardown just
        // cleared. It must stay the first thing this method does.
        if (sessionLib.isTearingDown()) {
          return
        }

        if (store && store.state) {

          if (store.state.apiKey) {
            delete store.state.apiKey
          }

          // KAN-39: strip the auth slice before writing to localStorage.
          // Tokens must not be double-persisted via this legacy path; the
          // canonical localStorage write happens in src/lib/auth.js (XOL-151).
          // The strip here keeps saveStore() from racing the lib's writes.
          const persistable = stripSensitiveState(store.state)
          localStorage.setItem(COOKIE, JSON.stringify(persistable))
/*
          if (IS_DEV) {
            console.log('Store saved: ', store.state)
          }
*/
        } else {
          this.$message({message: 'Error saving state, undefined state or store', type: 'error'})
        }
      },



      /**
       * XOL-446: a full reset, not the old five-key partial one.
       *
       * This used to reset isLoggedIn / profile / org / stats / statsError
       * and leave xolasClient, baseMap and the rest of the previous
       * session's state in place. replaceState() with a freshly built boot
       * state clears everything and re-applies the environment fields in one
       * step. `replaceState` is reactive in Vuex 3 and does not fire
       * subscribers, so it cannot trigger a persistedstate write —
       * vuex-persistedstate already calls it on every boot.
       */
      resetStore () {
        store.replaceState(sessionLib.buildBootState(null, bootEnvironment()))
      },


      // -- Stats stuff ------------------------------------------------

      /**
      * getStats
      * @param orgId Number: default null
      *
      * @return Object
      */
      getStats(orgId = null, force = false) {

        if (force || this.isEmptyObject(this.getStore().stats)) {
          this.refreshStats(orgId ? orgId : this.profile().organisation_id)
        }

        return this.getStore().stats
      },



      /**
      * refreshStats
      * @param orgId Number: default null
      * @param callback Function:  default null
      *
      * @return Object
      */
      refreshStats (orgId = null, callback = null) {

        // @TODO        this.$emit('update-stats')

        // XOL-446: the `reloadClient` flag and its re-entrant call back into
        // init() used to live here. It was armed on an unauthenticated boot,
        // consumed by the first login, and never re-armed — so a second login
        // in the same page load kept serving the first user's client org. It
        // also made canLogin() issue two overlapping GET system/config calls.
        // canLogin() now calls fetchFullClient() explicitly instead.

        if (!orgId && this.isTelco()) {
          orgId = this.org().id
        }

        var url = this.getEndpoint('stats' + (this.isTowerCo() ? '/all' : '/org') )

        axios.get(url).then(response => {

          if (!this.isUndefined(response.data[0])) {
            this.getStore().stats = response.data[0]
            this.getStore().statsError = false
            this.saveStore()
          }

          if (this.isFunction(callback)) {
            callback(response.data[0])
            return
          }

        })
        .catch(e => {
          console.error('Unable to load stats:', e)
          this.getStore().stats = {}
          this.getStore().statsError = true
        })

      },



      // -- MetaData for tables and table strucutures  ----------------------

      getMeta (key) {
        let array = this.getConfig().meta

        for (var i = 0; i < array.length; i++) {
//          console.log(array[i].hasOwnProperty(key), array[i][key])
          if (array[i].hasOwnProperty(key)) {
            return this.fixFields(array[i][key])
          }
        }
        return null
      },



      // Needed for _id with default values, since they cause 422 errors on create.
      fixFields (array) {

        if (array.struct) {

          for (let type in array.struct) {
            if (type.endsWith('_id') && array.struct[type] === 0) {
              array.struct[type] = null
            } else {

              let t = array.meta[type].type.toLowerCase()

              switch (t) {
                case 'integer':
                  if (!type.endsWith('_id') && !array.struct[type]) {
                    array.struct[type] = 0
                  }
                  break

                case 'double':
                  if (!array.struct[type]) {
                    array.struct[type] = 0.0
                  } else {
                    array.struct[type] = Number(array.struct[type])
                  }
                  break

              }
//              console.log('meta', type, t, array.struct[type])
            }
          }
        }

        return array
      },



      getTableStruct (table) {
        let struct = this.getMeta(table).struct

        if (struct) {

          return this.clone(struct)

        } else {
          let msg = 'Fetching structure for \'' + table + '\' returned null.'

          this.$message({message: msg, type: 'error'})
          console.error(msg)
        }

      },



      // -- Helpers for JavaSrcipt stuff -----------------------------------

      isArray (value) {
        return Array.isArray(value)
      },
      /*
      // Apparently not. Yet MDN says Yes.
      isBigInt (value) {
        return (typeof value === 'bigint')
      },
      */

      isBoolean (value) {
        return (typeof value === 'boolean')
      },



      isDate (value) {
        return (value instanceof Date)
      },



      isFunction (value) {
        return (typeof value === 'function')
      },



      isString (value) {
        return (typeof value === 'string')
      },



      isNull (value) {
        return (value === null)
      },



      isNumber (value) {
        return Number.isInteger(value) || Number.parseFloat(value)
      },



      isJsObject (value) {
        return (typeof value === 'object')
      },



      isUndefined (value) {
        return (typeof value === 'undefined')
      },



      getType (prim) {

        if (prim) {
          switch (prim) {
            case prim === Array:
              return 'array'
            case prim === Boolean:
              return 'boolean'
            case prim === Date:
              return 'date'
            case prim === Function:
              return 'function'
            case prim === Number:
              return 'number'
            case prim === String:
              return 'string'
            case prim === Object:
              return 'object'
            default:
              return 'undefined'
          }
        }
        return null
      },



      getPrimType (text) {
        let tx = text.toLowerCase()

        if (tx) {
          switch (tx) {
            case tx === 'array' || tx === 'list':
              return Array
            case tx === 'boolean' || tx === 'checkbox':
              return Boolean
            case tx === 'date' || tx === 'datetime':
              return Date
            case tx === 'number' || tx === 'integer' || tx === 'float' || tx === 'decimal':
              return Number
            case tx === 'string' || tx === 'text' || tx === 'text editor' || tx === 'html':
              return String
            default:
              return 'undefined'
          }
        }
        return null
      },



      setProp (target, source) {
        if (source) {
          target = source
        } else {
          target = null
        }

        return target
      },



      removeNullArray (list) {
        if (!list) {
          return null
        }

        for (var i = 0; i < list.length; i++) {

          if (!list[i]) {
            list.splice(i, 1)
          }

        }  // for

        return list
      },


      arrayFindIndex (arr, value) {
        if (arr && this.isArray(arr) && value) {

          for (let i = 0; i <= arr.length; i++) {
            if (arr[i] === value) {
              return i
            }

          } // for
        }

        return null
      },



      arrayRemoveIndex (arr, index) {

        if (!arr && !this.isArray(arr) && !index) {
          return null
        }

        if (arr.length > index) {
          arr.splice(index, 1)
        }
        return arr
      },



      trim (str) {
        return str.replace(/^\s+|\s+$/gm, '')
      },



      strIf (str1, str2 = '', append = null, append2 = false) {
        str2 = (str2 ? str2 : '')  // To handle null variables
        return str1 ? str1 + (append ? append : '') : str2 + (append && append2 ? append : '')
      },



      sum (a, p, callback = null) {
        var r = 0

        if (a && p) {
          for (var i = 0; i < a.length; i++) {
            if (callback) {
              r += callback(a[i], p, r)
            } else {
              r += Number(a[i][p])
            }
          }
        }

        return r
      },



      isEmptyObject (obj) {
        for (let key in obj) {

          if (obj.hasOwnProperty(key)) {
            return false
          }

        } // for

        return true
      },



      bool (value) {
        if (value) {
          if (value === 1 || value === true || value.toUpperCase() === 'YES' || value.toUpperCase() === 'TRUE') {
            return true
          } else {
            return false
          }
        } else {
          return null
        }
      },



      clone (data) {
        return JSON.parse(JSON.stringify(data))
      },


      /**
       * Quick a dirty pluraliser- for a more exact one, use FormattingMixins: pluralise
       */
      plural (singular, num, addNumber = false, plural = null) {
        let message = (Number(num) === 1 ? singular : (plural || `${singular}s`))
        return addNumber === false ? message : `${num} ${message}`
      },


      equalJson (obj1, obj2) {

        if (!obj1 || !obj2) {
          console.log('equalJson: obj1 or obj2 null')
          return false
        }
        let ret = true

        for (var i in obj2) {
           if (!obj1.hasOwnProperty(i) || obj2[i] !== obj1[i]) {
              ret = false
           }
        }

      return ret
    },


      // -- Misc. stuff ------------------------------------------------

      hasLandowner (site) {
        return (site && site.tenancy && site.tenancy.owner)
      },



      hasTenancy (site) {
        return (site && site.tenancy)
      },


      hasSingleTenancy (site) {
        return (this.hasTenancy(site) && !this.isArray(site.tenancy))
      },



      hasTenancyRenewal (site) {
        return (this.hasSingleTenancy(site.tenancy) && site.tenancy.renewal)
      },



      getUtilLabel (data = null) {

        let str = null

        if (data) {

          if (data.tenancy && data.tenancy.util) {
            str = (data.tenancy.util.shortName ? data.tenancy.util.shortName : (data.tenancy.util.name ? data.tenancy.util.name : 'Util.'))
          }
        }

/*
        if (!str && cfg && cfg.orgUtilities && cfg.orgUtilities.length === 1) {
          return cfg.orgUtilities[0].shortName
        } else {
          str = 'Util.'
        }
*/
        // XOL-446: read through appConfig rather than the old module-scope
        // `config` var this method used to close over directly.
        var utilConfig = appConfig.getConfig()

        if (!str && utilConfig && utilConfig.orgUtilities && utilConfig.orgUtilities.length === 1) {

          return utilConfig.orgUtilities[0].shortName

        } else {

          if (!str) {
            str = 'Util.'
          }

        }

        return str
      } // getUtilLabel

    }
  }
)

Vue.mixin(AccessMixins)

// KAN-39: allowlist auth-related routes; everything else needs a valid JWT.
// XOL-602/603/604: the guard lives in lib/authGuard.js (so a spec can run the real one);
// the password-link, forgot, invite and password-set pages are signed-out-only.
router.beforeEach(createAuthGuard({
  isAuthenticated: authLib.isAuthenticated,
  setPostLoginRedirect: authLib.setPostLoginRedirect,
  store: store
}))


// XOL-596 (R2.18 A, UF7): leaving with saves pending. The auth guard above has
// already let the navigation through; this one holds it while the field saves
// that no drawer guards for itself are flushed (and handles /logout). `appRef` is
// set once the app exists: the first navigation runs while it is being created.
let appRef = null
router.beforeEach(createLeaveGuard(() => appRef))
router.afterEach(() => { clearLeaveBypass() })

// XOL-629: Esc on a $confirm / $msgbox rejects with 'cancel', like its Cancel button.
installEscCancel()

/* eslint-disable no-new */
const app = new Vue({
  el: '#app',
  router,
  //  mixins: [AccessMixins],
  template: '<App/>',
  components: { App },
  data: store
})

// XOL-596: one popup per pause-flush of a request drawer's field saves, and the
// "page is out of date" prompt. Wired once, at app level, so a notice outlives
// the drawer that raised it.
installSyncNotices(app)
appRef = app

// XOL-596 (R2.18 B): reload / tab close / typed URL start the pending saves with
// fetch keepalive and raise the browser's own leave prompt while anything is unsaved.
installPageLeave(window)
